// Buffer pooling system for reducing memory allocation overhead
import { AUDIO_CONSTANTS } from '../../src/config/audio-constants.js';

export class BufferPool {
  private pools: Map<number, Uint8Array[]> = new Map();
  private maxPoolSize: number = AUDIO_CONSTANTS.DEFAULT_BUFFER_POOL_SIZE;
  private statistics = {
    checkouts: 0,
    checkins: 0,
    allocations: 0,
    reuses: 0
  };

  /**
   * Check out a buffer of the specified size
   * @param size The size of buffer needed
   * @returns A Uint8Array of the requested size
   */
  checkout(size: number): Uint8Array {
    this.statistics.checkouts++;

    // Round size up to nearest power of 2 for better pooling
    const poolSize = this.roundToPowerOfTwo(size);
    const pool = this.pools.get(poolSize) || [];

    if (pool.length > 0) {
      this.statistics.reuses++;
      const buffer = pool.pop()!;

      // Return a view of the exact size requested
      if (buffer.length === size) {
        return buffer;
      } else {
        return new Uint8Array(buffer.buffer, 0, size);
      }
    }

    // No buffer available, allocate new one
    this.statistics.allocations++;
    const newBuffer = new Uint8Array(poolSize);
    // Return a view of the exact size requested
    if (size === poolSize) {
      return newBuffer;
    } else {
      return new Uint8Array(newBuffer.buffer, 0, size);
    }
  }

  /**
   * Return a buffer to the pool for reuse
   * @param buffer The buffer to return
   */
  checkin(buffer: Uint8Array): void {
    this.statistics.checkins++;

    // Don't pool very small buffers (use MIN_BUFFER_LENGTH instead of FRAME_SIZE)
    if (buffer.length < AUDIO_CONSTANTS.MIN_BUFFER_LENGTH) {
      return;
    }

    // Get the actual buffer size (not the view size)
    const actualBufferSize = buffer.buffer.byteLength;
    const poolSize = this.roundToPowerOfTwo(actualBufferSize);
    const pool = this.pools.get(poolSize) || [];

    // Limit pool size to prevent memory bloat
    if (pool.length < this.maxPoolSize) {
      // If it's a view, create a full buffer from the same ArrayBuffer
      let bufferToStore: Uint8Array;
      if (buffer.byteOffset > 0 || buffer.length !== buffer.buffer.byteLength) {
        // It's a view, create full buffer from same ArrayBuffer
        bufferToStore = new Uint8Array(buffer.buffer, 0, buffer.buffer.byteLength);
      } else {
        // It's already a full buffer, store as-is
        bufferToStore = buffer;
      }
      pool.push(bufferToStore);
      this.pools.set(poolSize, pool);
    }
  }

  /**
   * Clear all pooled buffers
   */
  clear(): void {
    this.pools.clear();
  }

  /**
   * Get pool statistics
   */
  getStatistics() {
    const poolSizes = Array.from(this.pools.entries()).map(([size, pool]) => ({
      size,
      count: pool.length
    }));

    return {
      ...this.statistics,
      reuseRate:
        this.statistics.checkouts > 0
          ? ((this.statistics.reuses / this.statistics.checkouts) * 100).toFixed(2) + '%'
          : '0%',
      poolSizes,
      totalPooledBuffers: poolSizes.reduce((sum, p) => sum + p.count, 0),
      totalPooledMemory: poolSizes.reduce((sum, p) => sum + p.size * p.count, 0)
    };
  }

  /**
   * Round a number up to the nearest power of two
   */
  private roundToPowerOfTwo(n: number): number {
    if (n <= 0) {
      return 1;
    }

    // If already a power of two, return as-is
    if ((n & (n - 1)) === 0) {
      return n;
    }

    // Find next power of two
    let power = 1;
    while (power < n) {
      power <<= 1;
    }
    return power;
  }
}

// Singleton instance for global use
export const globalBufferPool = new BufferPool();
